/* IPOPU motion: progressive enhancement only; Shopify owns catalog and checkout. */
(() => {
  'use strict';
  const $ = (s, root = document) => root.querySelector(s);
  const $$ = (s, root = document) => [...root.querySelectorAll(s)];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = matchMedia('(min-width: 701px)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const easing = 'cubic-bezier(.22,1,.36,1)';
  const animations = new Set();
  const images = new WeakMap(), dialogs = new WeakMap(), menus = new WeakMap();
  const initialized = new WeakSet(), detailsReady = new WeakSet(), heroReady = new WeakSet();
  let navigationFocus;
  const canAnimate = () => !reduced.matches && typeof Element.prototype.animate === 'function';
  function animate(element, frames, options = {}) {
    if (!element || !canAnimate()) return Promise.resolve();
    const animation = element.animate(frames, { duration: 420, easing, ...options });
    animations.add(animation);
    return animation.finished.catch(() => {}).finally(() => animations.delete(animation));
  }
  function snapshot(image) {
    return image ? { src: image.getAttribute('src'), srcset: image.getAttribute('srcset'), sizes: image.getAttribute('sizes'), alt: image.alt } : null;
  }
  function applyImage(image, data) {
    ['srcset', 'sizes'].forEach(name => data[name] ? image.setAttribute(name, data[name]) : image.removeAttribute(name));
    image.src = data.src;
    if (data.alt !== undefined) image.alt = data.alt;
  }
  function preload(data) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      let settled = false;
      const finish = error => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        image.onload = image.onerror = null;
        error ? reject(error) : resolve(data);
      };
      const timer = setTimeout(() => finish(Error('Image loading timed out.')), 12000);
      image.onload = () => {
        if (image.decode) image.decode().catch(() => {}).then(() => finish());
        else finish();
      };
      image.onerror = () => finish(Error('This image could not be loaded.'));
      if (data.sizes) image.sizes = data.sizes;
      if (data.srcset) image.srcset = data.srcset;
      image.src = data.src;
    });
  }
  function commitImage(image, data) {
    if (!image || !data || !image.isConnected) return;
    const prior = images.get(image);
    prior?.ghost?.remove();
    let ghost;
    if (canAnimate() && image.complete && image.naturalWidth && image.getClientRects().length) {
      const parent = image.parentElement;
      const style = getComputedStyle(image);
      if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
      const box = image.getBoundingClientRect(), host = parent.getBoundingClientRect();
      ghost = image.cloneNode(false);
      ghost.removeAttribute('id');
      ghost.removeAttribute('srcset');
      ghost.removeAttribute('sizes');
      ghost.src = image.currentSrc || image.src;
      ghost.alt = '';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.className = 'ipopu-image-ghost';
      ghost.style.cssText = `position:absolute;pointer-events:none;left:${box.left-host.left-parent.clientLeft+parent.scrollLeft}px;top:${box.top-host.top-parent.clientTop+parent.scrollTop}px;width:${box.width}px;height:${box.height}px;max-width:none;margin:0;object-fit:${style.objectFit};object-position:${style.objectPosition};border-radius:${style.borderRadius};z-index:${style.zIndex};`;
      image.after(ghost);
    }
    applyImage(image, data);
    images.set(image, { ...prior, ghost });
    if (ghost) animate(ghost, [{ opacity: 1 }, { opacity: 0 }], { duration: 460 }).finally(() => ghost.remove());
  }
  async function swapImage(image, data) {
    if (!image || !data?.src) return { applied: false };
    const prior = images.get(image) || {};
    const token = (prior.token || 0) + 1;
    images.set(image, { ...prior, token });
    image.setAttribute('aria-busy', 'true');
    try {
      await preload(data);
      if (images.get(image)?.token !== token || !image.isConnected) return { applied: false };
      commitImage(image, data);
      return { applied: true };
    } catch (error) {
      return { applied: false, error: images.get(image)?.token === token ? error : null };
    } finally {
      if (images.get(image)?.token === token) image.removeAttribute('aria-busy');
    }
  }
  function dialogOpen(dialog) {
    const token = (dialogs.get(dialog) || 0) + 1;
    dialogs.set(dialog, token);
    dialog.getAnimations().forEach(a => a.cancel());
    dialog.classList.remove('is-closing');
    const drawer = dialog.classList.contains('cart-dialog');
    animate(dialog, [{ opacity: drawer ? 1 : 0, transform: drawer ? 'translateX(100%)' : 'translateY(18px) scale(.985)' }, { opacity: 1, transform: 'none' }], { duration: drawer ? 420 : 330 });
  }
  async function dialogClose(dialog) {
    if (!dialog?.open || dialog.classList.contains('is-closing')) return;
    const token = (dialogs.get(dialog) || 0) + 1;
    dialogs.set(dialog, token);
    const style = getComputedStyle(dialog);
    const start = { opacity: style.opacity, transform: style.transform };
    dialog.getAnimations().forEach(a => a.cancel());
    dialog.classList.add('is-closing');
    const drawer = dialog.classList.contains('cart-dialog');
    await animate(dialog, [start, { opacity: drawer ? 1 : 0, transform: drawer ? 'translateX(100%)' : 'translateY(12px) scale(.99)' }], { duration: 200, easing: 'cubic-bezier(.4,0,1,1)' });
    if (dialogs.get(dialog) !== token) return;
    dialog.classList.remove('is-closing');
    dialog.close();
  }
  async function menu(open) {
    const nav = $('#main-nav');
    if (!nav) return;
    const token = (menus.get(nav) || 0) + 1;
    menus.set(nav, token);
    const style = getComputedStyle(nav), start = { opacity: style.opacity, transform: style.transform };
    nav.getAnimations().forEach(a => a.cancel());
    if (desktop.matches) { nav.classList.remove('is-open'); nav.inert = false; nav.removeAttribute('aria-hidden'); return; }
    if (open) {
      const wasOpen = nav.classList.contains('is-open');
      nav.classList.add('is-open');
      nav.inert = false;
      nav.removeAttribute('aria-hidden');
      animate(nav, [wasOpen ? start : { opacity: 0, transform: 'translateY(-10px)' }, { opacity: 1, transform: 'none' }], { duration: 280 });
    } else {
      if (nav.contains(navigationFocus)) navigationFocus = null;
      nav.inert = true;
      nav.setAttribute('aria-hidden', 'true');
      if (nav.classList.contains('is-open')) await animate(nav, [start, { opacity: 0, transform: 'translateY(-8px)' }], { duration: 180 });
      if (menus.get(nav) === token) nav.classList.remove('is-open');
    }
  }
  const reveal = element => {
    element.classList.add('is-visible');
    element.classList.remove('reveal-ready');
    observer?.unobserve(element);
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => entries.forEach(entry => {
    if (entry.isIntersecting) reveal(entry.target);
  }), { threshold: 0, rootMargin: '0px 0px -25px 0px' }) : null;
  function initReveals(root = document) {
    const selectors = '.reveal,.section-heading,.native-collection>h1,.native-search>h1,.native-info>h1,.native-article>h1,.native-bundle>h1,.native-product .product-gallery,.native-product .detail-copy,.banner-copy,.fall-copy,.christmas-copy,.newsletter-copy,.color-layout,.spaces-layout';
    const candidates = new Set([...(root.matches?.(selectors) ? [root] : []), ...$$(selectors, root)]);
    const grids = '.offer-grid,.product-grid,.catalog-results,.occasion-grid,.bundle-steps,.style-grid,.journal-grid,.why-grid,.native-journal-grid,.spaces-dialog-grid,.look-products,.footer-grid';
    [...(root.matches?.(grids) ? [root] : []), ...$$(grids, root)].forEach(grid => {
      [...grid.children].forEach((child, index) => {
        const module = grid.matches('.offer-grid,.style-grid,.journal-grid');
        if (module) child.classList.add('ipopu-module-reveal');
        child.style.setProperty('--ipopu-reveal-delay', `${module ? Math.min(index, 2) * 90 : Math.min(index % 4, 3) * 65}ms`);
        candidates.add(child);
      });
    });
    candidates.forEach(element => {
      if (initialized.has(element)) return;
      initialized.add(element);
      element.classList.remove('reveal-ready');
      if (!canAnimate() || !observer || element.closest('dialog')) { reveal(element); return; }
      element.classList.add('ipopu-reveal');
      if (element.matches('.color-layout,.spaces-layout')) element.classList.add('ipopu-module-reveal');
      // Restored scroll position and anchor targets must never be hidden above the reader.
      if (element.getBoundingClientRect().bottom <= 0) reveal(element);
      else observer.observe(element);
    });
  }
  function heroIntro(root) {
    $$('.hero', root).forEach(hero => {
      if (heroReady.has(hero)) return;
      heroReady.add(hero);
      if (!canAnimate() || hero.getBoundingClientRect().bottom < 0 || scrollY > 60) return;
      $$('.hero-content > *', hero).forEach((element, index) => animate(element, [{ opacity: 0, transform: 'translateY(20px)' }, { opacity: 1, transform: 'none' }], { duration: 750, delay: 80 + index * 100, fill: 'backwards' }));
      animate($('.hero-bottom', hero), [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 600, delay: 350, fill: 'backwards' });
    });
  }
  function initDropdowns(root) {
    $$('.nav-dropdown', root).forEach(details => {
      if (detailsReady.has(details)) return;
      detailsReady.add(details);
      const panel = $('.dropdown-panel', details), summary = $('summary', details);
      let operation = 0, closeTimer;
      async function setOpen(open) {
        const token = ++operation;
        clearTimeout(closeTimer);
        if (open === details.open && !panel?.getAnimations().length) return;
        panel?.getAnimations().forEach(a => a.cancel());
        if (open) {
          $$('.nav-dropdown[open]', details.closest('.main-nav') || document).filter(other => other !== details).forEach(other => {
            $('.dropdown-panel', other)?.getAnimations().forEach(animation => animation.cancel());
            other.open = false;
          });
          details.open = true;
          animate(panel, [{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], { duration: 220 });
        } else {
          await animate(panel, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-4px)' }], { duration: 140 });
          if (token === operation) details.open = false;
        }
      }
      summary?.addEventListener('click', event => { event.preventDefault(); setOpen(!details.open); });
      details.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse' && desktop.matches && finePointer.matches) setOpen(true); });
      details.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse' && desktop.matches && finePointer.matches && !details.contains(document.activeElement)) closeTimer = setTimeout(() => setOpen(false), 130); });
      details.addEventListener('focusout', () => { closeTimer = setTimeout(() => { if (!details.contains(document.activeElement) && !details.matches(':hover')) setOpen(false); }, 80); });
      details.addEventListener('keydown', event => { if (event.key === 'Escape' && details.open) { event.preventDefault(); setOpen(false); summary.focus(); } });
    });
  }
  function init(root = document) { initReveals(root); initDropdowns(root); heroIntro(root); }
  document.addEventListener('focusin', event => {
    navigationFocus = event.target.closest('#main-nav,.mobile-toggle') ? event.target : null;
    if (!desktop.matches && !event.target.closest('.site-header') && $('.mobile-toggle')?.getAttribute('aria-expanded') === 'true') $('.mobile-toggle').click();
    let element = event.target.closest('.ipopu-reveal');
    while (element) { reveal(element); element = element.parentElement?.closest('.ipopu-reveal'); }
  });
  document.addEventListener('focusout', event => {
    // Retain a control hidden by a breakpoint until its visible replacement can receive focus.
    if (event.target === navigationFocus && (event.relatedTarget || event.target.getClientRects().length)) navigationFocus = null;
  });
  document.addEventListener('pointerdown', event => {
    if (!event.target.closest('#main-nav,.mobile-toggle')) navigationFocus = null;
  });
  document.addEventListener('click', event => {
    if (!event.target.closest('.nav-dropdown')) $$('.nav-dropdown[open]').forEach(details => { details.open = false; });
    if (!event.target.closest('.site-header') && $('.mobile-toggle')?.getAttribute('aria-expanded') === 'true') $('.mobile-toggle').click();
  });
  document.addEventListener('shopify:section:select', event => $$('.ipopu-reveal', event.target).forEach(reveal));
  document.addEventListener('shopify:block:select', event => { event.target.closest('.ipopu-reveal') && reveal(event.target.closest('.ipopu-reveal')); });
  document.addEventListener('shopify:section:unload', event => {
    $$('.ipopu-reveal', event.target).forEach(element => observer?.unobserve(element));
    $$('.ipopu-image-ghost', event.target).forEach(element => element.remove());
  });
  desktop.addEventListener('change', () => {
    const toggle = $('.mobile-toggle'), nav = $('#main-nav'), focused = document.activeElement === document.body ? navigationFocus : document.activeElement;
    const focusToggle = !desktop.matches && nav?.contains(focused), focusNav = desktop.matches && focused === toggle;
    if (toggle?.getAttribute('aria-expanded') === 'true') toggle.click();
    menu(false);
    if (focusToggle) toggle?.focus({ preventScroll: true });
    else if (focusNav) $('a,summary', nav)?.focus({ preventScroll: true });
  });
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      animations.forEach(animation => animation.cancel());
      $$('.ipopu-reveal').forEach(reveal);
      $$('.ipopu-image-ghost').forEach(element => element.remove());
    }
  });
  window.IPOPUMotion = { init, snapshot, preload, commitImage, swapImage, dialogOpen, dialogClose, menu };
})();
